import { useRef, useState, useCallback } from 'react'
import { useCascadeStore } from '../store'
import { CascadeNode } from './CascadeNode'
import { CascadeEdges } from './CascadeEdges'
import { ChoiceBubbles } from './ChoiceBubbles'

export function CascadeCanvas() {
  const {
    nodes, zoom, panX, panY, setZoom, setPan,
    selectNode, cancelConnecting, connectingFrom,
    activeChoice, dismissChoices,
  } = useCascadeStore()
  const canvasRef = useRef<HTMLDivElement>(null)
  const [isPanning, setIsPanning] = useState(false)
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.1 : 0.1
      setZoom(zoom + delta)
    } else {
      setPan(panX - e.deltaX, panY - e.deltaY)
    }
  }, [zoom, panX, panY, setZoom, setPan])

  const handleMouseDown = (e: React.MouseEvent) => {
    // If the click landed on a node, button, or interactive element, bail out
    const target = e.target as HTMLElement
    if (target.closest('[data-node]') || target.closest('button') || target.closest('[data-action]')) return

    // Dismiss any active choices or connecting mode
    if (activeChoice) {
      dismissChoices()
      return
    }
    if (connectingFrom) {
      cancelConnecting()
      return
    }
    selectNode(null)

    // Pan with click-drag
    if (e.button === 0 || e.button === 1) {
      e.preventDefault()
      setIsPanning(true)
      panStart.current = { x: e.clientX, y: e.clientY, panX, panY }

      const handleMouseMove = (e: MouseEvent) => {
        const dx = e.clientX - panStart.current.x
        const dy = e.clientY - panStart.current.y
        setPan(panStart.current.panX + dx, panStart.current.panY + dy)
      }

      const handleMouseUp = () => {
        setIsPanning(false)
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }
  }

  return (
    <div
      ref={canvasRef}
      className={`
        relative w-full h-full overflow-hidden
        bg-[var(--bg-primary)]
        ${isPanning ? 'cursor-grabbing' : connectingFrom ? 'cursor-crosshair' : 'cursor-grab'}
      `}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      style={{
        backgroundImage: `
          radial-gradient(circle, var(--border-color) 1px, transparent 1px)
        `,
        backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
        backgroundPosition: `${panX}px ${panY}px`,
      }}
    >
      {/* Connection mode indicator */}
      {connectingFrom && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 px-4 py-1.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-400 text-xs font-medium backdrop-blur-sm">
          Click a target node to connect, or click canvas to cancel
        </div>
      )}

      {/* Transformed content */}
      <div
        style={{
          transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
        className="absolute inset-0"
      >
        {/* Edges layer */}
        <CascadeEdges />

        {/* Nodes layer */}
        {nodes.map(node => (
          <CascadeNode
            key={node.id}
            node={node}
            isSelected={useCascadeStore.getState().selectedNodeId === node.id}
            isConnecting={!!connectingFrom}
            zoom={zoom}
          />
        ))}

        {/* Choice bubbles (animated fan-out) */}
        <ChoiceBubbles />
      </div>

      {/* Zoom controls */}
      <div className="absolute bottom-4 left-4 flex items-center gap-1 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] shadow-lg p-1 z-40">
        <button
          onClick={() => setZoom(zoom - 0.1)}
          className="px-2 py-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          -
        </button>
        <span className="text-[10px] text-[var(--text-muted)] w-10 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => setZoom(zoom + 0.1)}
          className="px-2 py-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          +
        </button>
        <div className="w-px h-4 bg-[var(--border-color)]" />
        <button
          onClick={() => { setZoom(1); setPan(0, 0) }}
          className="px-2 py-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          Reset
        </button>
      </div>
    </div>
  )
}
